import { ReactElement, Suspense, useLayoutEffect, useRef } from 'react';
import { Box, Button, LoadingContainer } from '@ff-it/ui';
import * as styles from './CommentStream.css';
import { StreamController, useStreamController } from './useStreamController';
import { restoreScrollPosition, useScrollAnchor } from './useScrollAnchor';
import { CommentCard } from './CommentCard';
import { CommentForm } from './CommentForm';
import { Topic } from './types';
import { useNotificationSeen } from 'modules/notifications';

export function CommentList({ streamController }: { streamController: StreamController }): ReactElement {
  const { anchor, items, loadOlder, loadNewer, scrollAction } = streamController;
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollState = useScrollAnchor(containerRef);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refs are fine
  useLayoutEffect(() => {
    const action = scrollAction.current;
    const container = containerRef.current;
    if (!action || !container || !items.length) {
      return;
    }

    switch (scrollAction.current) {
      case 'END': {
        container.scrollTop = container.scrollHeight;
        return;
      }
      case 'NEW': {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        });
        return;
      }
      case 'RESTORE': {
        if (scrollState) {
          restoreScrollPosition(container, scrollState);
        }
        return;
      }
      case 'ANCHOR': {
        if (anchor) {
          restoreScrollPosition(container, {
            itemId: anchor,
            // should be good enough, but it's bottom of the card, not top
            offset: container.clientHeight / 2,
          });
        }
      }
    }

    scrollAction.current = null;
  }, [items, anchor]);

  return (
    <div className={styles.listContainer} ref={containerRef}>
      {loadOlder && (
        <Button onClick={loadOlder} size="sm" variant="secondary" iconAfter="arrow-up" mx="auto">
          Load older messages
        </Button>
      )}
      {items.length === 0 && (
        <Box color="gray500" textSize="sm" alignSelf="center" mt="auto" mb="auto">
          There are no comments yet
        </Box>
      )}
      {items.map((item) => (
        <CommentCard key={item.id} item={item} selected={item.id === anchor} />
      ))}
      {loadNewer && (
        <Button onClick={loadNewer} size="sm" variant="secondary" iconAfter="arrow-down" mx="auto">
          Load newer messages
        </Button>
      )}
    </div>
  );
}

type CommentStreamProps = {
  topic: Topic;
  onTopicUpdate: (topic: Topic) => void;
  commentId: number | null;
};
export function CommentStream({ topic, onTopicUpdate, commentId }: CommentStreamProps): ReactElement {
  useNotificationSeen(topic.id ? `comments:topic:${topic.id}` : null);

  const streamController = useStreamController(topic, onTopicUpdate, commentId);

  return (
    <LoadingContainer loading={streamController.isLoading} className={styles.loaderContainer}>
      <CommentList streamController={streamController} />

      {/* editor is lazy */}
      <Suspense>
        <CommentForm onCreate={streamController.onCreate} />
      </Suspense>
    </LoadingContainer>
  );
}
